/**
 * Work captured on a device that has not reached the server yet.
 *
 * THE ONE RULE, and everything else is scaffolding around it: an item leaves
 * the queue ONLY when the server confirms it with a 2xx. Not when the request
 * returns, not when it throws, not when the flush finishes — when the server
 * says yes. A queue that drops on failure destroys exactly the record it
 * exists to protect, and does it at the moment the person believes they are
 * saving their work.
 *
 * THERE ARE TWO LISTS, and the second one is the honest end of that rule.
 * `pending-captures:<user>` is what is still going to send. `pending-blocked:<user>` is what
 * the server READ and refused — a 400, a 409, a 422 — which will never send on
 * its own however many times it is retried. Those are moved out of the queue
 * so the count goes down and the retry loop ends, and they are KEPT so a
 * person can find them. They used to be handed to a toast and deleted, which
 * meant whoever was looking at another tab when the flush ran lost the capture
 * and every trace of it. The only thing that may now destroy a capture is a
 * 2xx or a person saying so.
 *
 * WHY localStorage AND NOT IndexedDB. This is a safety net, not a sync engine.
 * localStorage is synchronous, so a write cannot be half-finished by a tab
 * closing mid-transaction, and the whole queue is one JSON string that either
 * parses or does not. IndexedDB buys size and async writes, and both of those
 * are the beginning of the engine we are deliberately not building. The cost
 * is the ~5MB origin cap, which is why nothing large is ever queued — see
 * the note on attachments below.
 *
 * NOTHING LARGE GOES IN HERE. A phone photo as base64 is 1-3MB and the cap is
 * shared with everything else on the origin, so two photos can fill it. The
 * failure mode of a full quota is a throw MID-WRITE, which would lose the very
 * record being protected. So enqueue THROWS rather than swallowing it, and a
 * caller must refuse the capture and say so: telling somebody their device is
 * full is kinder than accepting their work and dropping it.
 *
 * PHOTOS GO IN INDEXEDDB, and an item holds only the id of one — see
 * services/pendingBlobs.ts, which owns the downscale, the per-photo cap and
 * the refusal when the device is full. The earlier reading of the rule above
 * was that a photo could not be queued at all and each surface had to refuse
 * it; that was honest but it meant a technician standing in a plant room could
 * not record what they were looking at. The rule was never "no photos", it was
 * "nothing large in localStorage", and it is unchanged.
 *
 * 🔴 IF YOU CALL THIS FROM useMutation, SET networkMode: 'always'.
 *
 * React Query's default is networkMode: 'online', and offline that does not
 * mean "fail" — it means the mutation is PAUSED and `mutationFn` is never
 * called at all. isPending stays true, no request is made, and nothing reaches
 * this module: the capture sits in a JS object that dies with the tab, which
 * is the exact loss the queue exists to prevent, wearing a spinner that says
 * the work is being saved.
 *
 * It is not theoretical. Every queue-backed mutation shipped in this change
 * had it, and no unit test could see it — jsdom's navigator.onLine is true, so
 * the gate never closes. It took a live run with the connection actually cut.
 * `pendingQueueStep2.test.ts` now asserts the flag is present at every call
 * site, which is the only thing a test can do about it.
 *
 * A RETRY MUST NOT CREATE A SECOND ROW. Every item carries an idempotency key,
 * minted once when it is captured and sent on every attempt of that same item.
 * The one case it exists for is the ambiguous timeout: the request arrived,
 * the work was done, the reply never came back. The item is still here — it
 * has to be, because from here that is indistinguishable from a request that
 * never left — and the next flush sends it again. The key is what stops that
 * second send producing a second walk-in visitor at the front desk. The server
 * side is backend/utils/idempotency.py.
 */

import { apiUrl, getAccessToken, getSignedInUserId } from '@/utils/api';
import { clearKioskDevice, getKioskDevice, isKioskDevicePath, kioskQueueOwner } from '@/utils/kioskDevice';
import { notifyWrite } from '@/utils/refreshAfterWrite';
import { formatDetailList } from '@/utils/validation';
import { deletePhoto, getPhoto } from './pendingBlobs';

/**
 * 🔴 ONE QUEUE PER PERSON (FM Technician A16, decision D8). The queue used to be
 * one list per DEVICE, and a shared phone handed from the night technician to
 * the day one replayed the night's captures under the day's login — wrong
 * name on the record, and a 403 when the job was not theirs. Every list is now
 * keyed by the signed-in user's id, and a flush only ever sends the list of
 * whoever is signed in, under their own token.
 *
 * The unkeyed keys below are what the first version of this module wrote.
 * Their items have no owner, so they are NEVER sent on their own: the next
 * person to sign in finds them in their blocked list, marked "From an earlier
 * sign-in — check before sending", and decides. See adoptLegacy.
 */
const LEGACY_KEY = 'pending-captures';
const queueKey = (uid: string) => `pending-captures:${uid}`;

/**
 * Captures the server REFUSED, kept until a person deals with them.
 *
 * A second key rather than a flag on the first, because the two lists answer
 * different questions and are read by different code paths: "what is still
 * going to send" and "what is never going to send on its own". A flag would
 * make every reader of the queue remember to filter, and the one that forgot
 * would retry a body the server has already rejected, for ever.
 */
const LEGACY_BLOCKED_KEY = 'pending-blocked';
const blockedKey = (uid: string) => `pending-blocked:${uid}`;

/** What an ownerless item from before per-person queues says in the panel. */
export const EARLIER_SIGN_IN = 'From an earlier sign-in — check before sending';

/** The token an item goes under: a tablet's own on its own routes (R4). */
const bearerFor = (endpoint: string): string | null | undefined =>
  isKioskDevicePath(endpoint) ? getKioskDevice()?.token : getAccessToken();

/** One captured action waiting for the server. */
export interface PendingItem {
  /** Stable id, so a flush that runs twice cannot remove the wrong row. */
  id: string;
  /** What the user did, for the banner: "Inspection", "Check-in". */
  label: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH';
  body: Record<string, unknown>;
  /**
   * IndexedDB id of a photo that goes with this item, from pendingBlobs.
   * Its presence changes how the item is SENT: multipart, with the body's
   * fields alongside the file, rather than JSON.
   */
  blobRef?: string;
  /**
   * Sent as `Idempotency-Key` on every attempt of this item, and never
   * regenerated. Optional only because items queued by the first version of
   * this module predate it; `keyFor` falls back to the item id, which is
   * equally stable and equally unique.
   */
  idempotencyKey?: string;
  /** When it was captured, not when it was last tried. */
  capturedAt: number;
  attempts: number;
  /**
   * Queued ON PURPOSE while online, behind other work (FM Technician B8: a
   * finish waiting for its photos). The person has left the screen, so even
   * its FIRST send refused is a refusal they never saw — reported as a
   * conflict like a replay (D8: the FM sees both).
   */
  deferred?: boolean;
  /** The last failure, shown to the person rather than kept in a console. */
  lastError?: string;
  /** Server errors (5xx) in a row on this item; any other outcome resets it.
   *  At SERVER_FAILURE_LIMIT the item is moved to blocked (review R2-3 #2). */
  serverFailures?: number;
}

/** The key this item sends, including for one captured before keys existed.
 *  Stable by construction: the id is minted once, at capture. */
export function keyFor(item: PendingItem): string {
  return item.idempotencyKey || item.id;
}

function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Old Safari, and any non-secure origin. 128 bits is not the requirement —
  // the server scopes keys to the signed-in user, so this only has to be
  // unique among one person's own captures.
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}` +
    `-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * A capture the server refused outright.
 *
 * NOT a failure to reach the server — that stays in the queue and retries
 * itself. This is a body the server READ and would not accept: a 400, a 404, a
 * 409, a 422. Retrying it changes nothing, which is why it is moved out of the
 * queue; keeping it in would mean a count that never goes down and a retry
 * loop that never succeeds.
 *
 * It is KEPT, though, and that is the change. It used to be handed to a toast
 * and deleted — so a person who was looking at another tab when the flush ran
 * lost the capture and every trace that it had ever existed. The work is the
 * whole point of this module; the only thing that may destroy it is a person
 * saying so.
 */
export interface BlockedItem extends PendingItem {
  /** When the server refused it, which is not when it was captured. */
  blockedAt: number;
  /** The status it was refused with, so the panel can say more than "failed". */
  status?: number;
  /** Captured before the queue was kept per person, so nobody knows whose it
   *  is. Never sent without a person pressing Send (A16). */
  fromEarlierSignIn?: boolean;
  /** Moved out of the queue WITHOUT a refusal (review R2-3 #2): the server
   *  kept failing on it, or a person moved it so the work behind it can go.
   *  The panel shows its reason as is, not as "Not accepted". */
  heldBack?: boolean;
}

/** 🔴 A head item the server keeps failing on (5xx) would hold back every
 *  later capture of that person for ever, on every page (strict FIFO, R2-2).
 *  After this many server errors in a row — or one on an item over a day old —
 *  it is moved to blocked, kept, and the queue moves on (review R2-3 #2). */
export const SERVER_FAILURE_LIMIT = 5;
export const SERVER_FAILURE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const SERVER_KEPT_FAILING = 'The server kept failing on this — kept here, not sent.';
export const MOVED_BY_YOU = 'Moved to Not sent by you — kept here, not sent.';

/** Both lists, together. They are always read as a pair — a count that showed
 *  one without the other would be a lie in whichever direction it left out. */
export interface QueueState {
  /** Going to send on its own, as soon as there is a connection. */
  waiting: PendingItem[];
  /** Not going to send without a person. */
  blocked: BlockedItem[];
}

/** What a whole flush did. All three matter to the caller: `sent` is worth
 *  celebrating, `kept` is worth waiting for, and `refused` is the one that
 *  needs somebody — and the one whose omission used to make a refusal silent. */
export interface FlushResult {
  sent: number;
  kept: number;
  refused: number;
  /** Whose list this flush sent (review R2-4 #2). A flush that was out when
   *  its owner signed out ends under the next person; its result is not
   *  theirs to be told. Null when nobody was signed in. */
  uid: string | null;
}

/** What one send attempt did. `sent` is the only field that means the server
 *  confirmed it; everything else describes a capture that is still on the
 *  device. */
export interface SendResult {
  sent: boolean;
  /** The response body on a confirmed send, unwrapped from the API envelope.
   *  Undefined when nothing was sent, and when the body did not parse. */
  data?: unknown;
  /** The envelope's `message` on a confirmed send — a route that has
   *  something to SAY about the write (Item #9: whether the contractor was
   *  emailed) says it here. */
  message?: string;
  error?: string;
  permanent?: boolean;
}

type Listener = (state: QueueState) => void;
const listeners = new Set<Listener>();

function emit(): void {
  const state = getState();
  listeners.forEach((l) => {
    try {
      l(state);
    } catch {
      /* a broken subscriber must not stop the others being told */
    }
  });
}

function readList<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/**
 * Hand ownerless items from the first version of this module to whoever is
 * signed in now — as BLOCKED, never as waiting, so nothing goes out under a
 * login that may not be the one that captured it. The person sees each one
 * with EARLIER_SIGN_IN and either sends it (retryBlocked) or discards it.
 *
 * Written straight to storage, with no emit: emit reads the state, which would
 * come back here while the legacy keys still exist. The blocked list is written
 * BEFORE the legacy keys are removed, so a failed write loses nothing.
 */
function adoptLegacy(uid: string): void {
  let legacy: PendingItem[];
  try {
    if (localStorage.getItem(LEGACY_KEY) === null && localStorage.getItem(LEGACY_BLOCKED_KEY) === null) return;
    legacy = [...readList<PendingItem>(LEGACY_KEY), ...readList<BlockedItem>(LEGACY_BLOCKED_KEY)];
  } catch {
    return;
  }
  const now = Date.now();
  const mine = readList<BlockedItem>(blockedKey(uid));
  const adopted: BlockedItem[] = legacy
    .filter((i) => i && typeof i.id === 'string' && !mine.some((m) => m.id === i.id))
    .map((i) => ({ ...i, lastError: EARLIER_SIGN_IN, blockedAt: now, fromEarlierSignIn: true }));
  try {
    localStorage.setItem(blockedKey(uid), JSON.stringify([...mine, ...adopted]));
    localStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(LEGACY_BLOCKED_KEY);
  } catch {
    /* no room: they stay where they are, unsent, and are offered next time */
  }
}

/** The signed-in person whose lists are read and written, or null. */
function owner(): string | null {
  const uid = getSignedInUserId();
  if (uid) adoptLegacy(uid);
  // Reception R4: a kiosk tablet has no person; the device owns its own list.
  return uid ?? kioskQueueOwner();
}

/**
 * Reception R4: a tablet re-set up with a new device code. What an earlier device
 * on this tablet left behind has no owner who will ever send it, so it comes to
 * this device's blocked list for reception to look at (as adoptLegacy does),
 * never replayed under a device that may be at another building.
 */
export function adoptEarlierDevices(uid: string): void {
  for (const key of Object.keys(localStorage)) {
    const old = /^pending-(captures|blocked):(kiosk-device:.+)$/.exec(key);
    if (!old || old[2] === uid) continue;
    const items = readList<PendingItem>(key);
    const mine = readList<BlockedItem>(blockedKey(uid));
    const adopted = items.map((i) => ({ ...i, reason: EARLIER_SIGN_IN } as unknown as BlockedItem));
    localStorage.setItem(blockedKey(uid), JSON.stringify([...mine, ...adopted]));
    localStorage.removeItem(key);
  }
}

/** Everything still waiting for the signed-in person. Never throws: an
 *  unreadable queue reads as empty rather than taking the app down, and the
 *  raw string is left alone so a later version can still look at it. Signed
 *  out, there is nothing to send, so it is empty. */
export function getPending(uid: string | null = owner()): PendingItem[] {
  return uid ? readList<PendingItem>(queueKey(uid)) : [];
}

/** Everything the server refused, for the signed-in person. Same contract. */
export function getBlocked(uid: string | null = owner()): BlockedItem[] {
  return uid ? readList<BlockedItem>(blockedKey(uid)) : [];
}

export function getState(): QueueState {
  const uid = owner();
  return { waiting: getPending(uid), blocked: getBlocked(uid) };
}

/** How many captures a person has not got to the server yet — both lists.
 *  Sign-out asks before leaving any behind (A16). */
export function unsentCount(uid: string | null = owner()): number {
  return getPending(uid).length + getBlocked(uid).length;
}

/** Tell every subscriber to re-read: someone signed in or out, so the lists
 *  shown are now somebody else's. Called by the auth store. */
export function queueUserChanged(): void {
  emit();
}

/**
 * Ask the browser to keep this origin's storage when the device runs short
 * (FM Technician A16, decision D8) — without it, a phone low on space may
 * evict IndexedDB and localStorage, which is exactly where a technician's
 * unsent readings and photos are. Asked for a field worker only: somebody who
 * WORKS jobs (maintenance:work) without reading the whole register
 * (maintenance:view_all) — permission, never role name. Best effort; a browser
 * without the API, or one that says no, changes nothing else.
 */
export function keepStorageForFieldWork(permissions: readonly string[] | null | undefined): void {
  if (!permissions || !permissions.includes('maintenance:work') || permissions.includes('maintenance:view_all')) return;
  try {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined;
    if (storage && typeof storage.persist === 'function') void storage.persist().catch(() => false);
  } catch {
    /* not available here */
  }
}

export class NotSignedInError extends Error {
  constructor() {
    super('You are signed out, so this could not be saved on this device. Sign in again and redo it.');
    this.name = 'NotSignedInError';
  }
}

function write(uid: string, items: PendingItem[]): void {
  localStorage.setItem(queueKey(uid), JSON.stringify(items));
  emit();
}

/** A failed write here is NOT swallowed: the caller of enqueue turns it into a
 *  refused capture. The blocked list is different — it is only ever written
 *  while moving something OUT of the queue, and a throw there would leave the
 *  item in neither list. So it is written first, and the queue is only trimmed
 *  once it is safely stored. See `block`. */
function writeBlocked(uid: string, items: BlockedItem[]): void {
  localStorage.setItem(blockedKey(uid), JSON.stringify(items));
  emit();
}

/** Subscribe to either list changing. Returns an unsubscribe. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(getState());
  return () => listeners.delete(listener);
}

export class QueueFullError extends Error {
  constructor() {
    super(
      'This device has no room left to save unsent work. Send what is already ' +
      'queued, or free up space, before capturing more.',
    );
    this.name = 'QueueFullError';
  }
}

/**
 * Put something in the queue.
 *
 * Called BEFORE the request goes out, so a tab that dies mid-request still has
 * the record. If the queue cannot be written — the storage cap — this THROWS,
 * and the caller must refuse the capture rather than proceed: an accepted
 * capture that was never stored is the silent loss this module exists to end.
 */
export function enqueue(
  item: Omit<PendingItem, 'id' | 'capturedAt' | 'attempts'>,
): PendingItem {
  const uid = owner();
  // Nobody to own it means nobody it may be sent as (A16).
  if (!uid) throw new NotSignedInError();
  const full: PendingItem = {
    ...item,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    idempotencyKey: item.idempotencyKey || newIdempotencyKey(),
    capturedAt: Date.now(),
    attempts: 0,
  };
  const next = [...getPending(uid), full];
  try {
    write(uid, next);
  } catch {
    // QuotaExceededError, or storage disabled. Either way this was not saved.
    throw new QueueFullError();
  }
  return full;
}

/**
 * The server confirmed it. This is the ONLY place a capture and its photo are
 * destroyed without a person asking, and it is reachable from one line in
 * `attempt` — the 2xx branch.
 *
 * The photo goes with the item and only with the item. Deleting it anywhere
 * else — on a failed send, on a flush that gave up, on a refusal somebody
 * might still want to retry — would destroy the evidence while the record that
 * needs it is still on the device.
 */
function removeConfirmed(uid: string, id: string): void {
  const item = getPending(uid).find((i) => i.id === id);
  write(uid, getPending(uid).filter((i) => i.id !== id));
  if (item?.blobRef) void deletePhoto(item.blobRef);
}

/**
 * Move a refused capture out of the queue and into the blocked list.
 *
 * THE ORDER MATTERS, for the same reason it matters in `captureAndSend`: the
 * blocked list is written FIRST and the queue is trimmed only once that
 * succeeded. Trimming first and then failing to write would lose the capture
 * in the half-second between the two — which is the exact failure this whole
 * module exists to make impossible, and it would happen on the path handling
 * something that has already gone wrong.
 *
 * The photo is NOT deleted. A blocked item can be retried by a person once
 * whatever the server objected to is fixed, and it would be worthless without
 * the photograph it was taken to carry.
 */
function block(
  uid: string, item: PendingItem, detail: string, status?: number,
  opts: { heldBack?: boolean; countAttempt?: boolean } = {},
): boolean {
  const { serverFailures: _drop, ...rest } = item;
  void _drop;
  const entry: BlockedItem = {
    ...rest,
    attempts: item.attempts + (opts.countAttempt === false ? 0 : 1),
    lastError: detail,
    blockedAt: Date.now(),
    ...(status !== undefined ? { status } : {}),
    ...(opts.heldBack ? { heldBack: true } : {}),
  };
  try {
    writeBlocked(uid, [...getBlocked(uid).filter((b) => b.id !== item.id), entry]);
  } catch {
    // No room to record the refusal. Leaving it in the queue is the lesser
    // harm: it retries and fails, which is visible and annoying, rather than
    // vanishing, which is neither.
    return false;
  }
  write(uid, getPending(uid).filter((i) => i.id !== item.id));
  return true;
}

/**
 * A person moves a WAITING item to Not sent (review R2-3 #2) — the manual way
 * past a head item that will not go, so the work queued behind it can. Kept,
 * never deleted: it can be sent again from the blocked list. Not while its
 * request is out (it might land).
 */
export function moveToBlocked(id: string): boolean {
  const uid = owner();
  if (!uid || inFlight.has(id)) return false;
  const item = getPending(uid).find((i) => i.id === id);
  if (!item) return false;
  const last = item.lastError ? ` Last try: ${item.lastError}` : '';
  return block(uid, item, `${MOVED_BY_YOU}${last}`, undefined, { heldBack: true, countAttempt: false });
}

/**
 * A person has decided this refused capture is not worth keeping.
 *
 * The only path in the module that destroys work the server never accepted, so
 * it takes an explicit call from an explicit confirmation, and it is never
 * reached by a retry, a flush, or a sweep.
 */
export function discardBlocked(id: string): void {
  const uid = owner();
  if (!uid) return;
  const item = getBlocked(uid).find((b) => b.id === id);
  writeBlocked(uid, getBlocked(uid).filter((b) => b.id !== id));
  if (item?.blobRef) void deletePhoto(item.blobRef);
}

function recordFailure(uid: string, id: string, message: string, serverError = false): void {
  write(
    uid,
    getPending(uid).map((i) =>
      i.id === id
        ? { ...i, attempts: i.attempts + 1, lastError: message, serverFailures: serverError ? (i.serverFailures ?? 0) + 1 : 0 }
        : i,
    ),
  );
}


/**
 * Refusals that have happened since anyone last looked, for the TOAST.
 *
 * Cleared on read, and deliberately separate from the blocked list, because
 * the two do different jobs and a person needs both: told at the moment it
 * happens, and able to find it again afterwards. Draining this one is what
 * stops the same refusal being toasted on every flush; the durable record is
 * `getBlocked()`, which is not cleared by reading it.
 */
/** A refusal waiting for its toast, with whose capture it was (review R2-4
 *  #2): the refusals of person A's flush must never be told to person B. */
export interface RejectedItem extends PendingItem {
  uid: string;
}

const rejected: RejectedItem[] = [];

/**
 * Drain the refusals for the toast. With a uid, only that person's are
 * returned; everyone else's are dropped with them — their owner is not the
 * one signed in, and the blocked list keeps each one for when they are back.
 * Without one, everything is drained (sign-out, tests).
 */
export function takeRejected(uid?: string | null): RejectedItem[] {
  const all = rejected.splice(0, rejected.length);
  return uid === undefined ? all : all.filter((r) => r.uid === uid);
}

/**
 * Told when a REPLAY is refused with a 409 or 422 (FM Technician B18, D8):
 * the job changed while the phone was offline. The item is in the blocked
 * list already; the listener (services/offlineSync reportConflict, wired for
 * the technician by TechMeEffects) also keeps the payload on the server so the
 * FM sees both and nothing depends on this phone surviving. A first send
 * refused while online is not a replay — the person sees that refusal at
 * once — so only items tried before (`attempts > 0`) are passed on.
 */
export type ReplayRefusedListener = (item: PendingItem, detail: string, status: number) => void;
let replayRefused: ReplayRefusedListener | null = null;
export function onReplayRefused(listener: ReplayRefusedListener | null): void {
  replayRefused = listener;
}

/** Send one item, removing it ONLY on a confirmed 2xx. The single place an
 *  item is ever removed for success, shared by captureAndSend and flush —
 *  a caller cannot be trusted with the rule if it can reach `remove` itself,
 *  so it cannot. */
/**
 * Items whose request is out right now, from this tab. A second send of the
 * same item while the first is still out is the "in flight" case the server
 * answers with a 425; it is never started from here — flush stops at it.
 */
const inFlight = new Set<string>();

/** Why a waiting row cannot be moved to Not sent: its request is out, and it
 *  might land (review R2-4 #4). */
export const BEING_SENT = 'Being sent now — wait for the answer';

/** Is this item's request out right now (from this tab)? */
export function isInFlight(id: string): boolean {
  return inFlight.has(id);
}

async function attempt(uid: string, item: PendingItem): Promise<SendResult> {
  if (inFlight.has(item.id)) {
    return { sent: false, error: 'Already being sent.' };
  }
  inFlight.add(item.id);
  // Told, so a row can say it is being sent now and not offer to move it
  // (review R2-4 #4) — and told again when the answer is in.
  emit();
  try {
    const r = await attemptOnce(uid, item);
    // A caller waiting on this item through an ordered flush gets its answer
    // (the kiosk needs the badge number the server issued).
    if (awaited.has(item.id)) awaited.set(item.id, r);
    return r;
  } finally {
    inFlight.delete(item.id);
    emit();
  }
}

/** Items a caller is waiting on through an ordered flush, with their answer
 *  once there is one. Only ever holds items somebody asked about. */
const awaited = new Map<string, SendResult | null>();

async function attemptOnce(uid: string, item: PendingItem): Promise<SendResult> {
  // 🔴 ONLY AS ITS OWNER (A16). A flush that started under one login must not
  // finish under the next one: the token below is whoever is signed in NOW.
  if ((getSignedInUserId() ?? kioskQueueOwner()) !== uid) {
    return { sent: false, error: 'Signed out before it could be sent; it stays with its owner.' };
  }
  let body: BodyInit;
  const headers: Record<string, string> = {
    // The same key on every attempt of the same item. See the header.
    'Idempotency-Key': keyFor(item),
    ...(bearerFor(item.endpoint) ? { Authorization: `Bearer ${bearerFor(item.endpoint)}` } : {}),
  };

  if (item.blobRef) {
    const photo = await getPhoto(item.blobRef);
    if (!photo) {
      // The blob is gone — cleared site data, a wiped profile — and it is not
      // coming back. Sending the row without its photo would file evidence
      // with no evidence in it, which is worse than saying so: this is handed
      // to `rejected` and named, not dropped in silence.
      const detail = 'The photo is no longer on this device and could not be sent.';
      rejected.push({ ...item, uid, lastError: detail });
      block(uid, item, detail);
      return { sent: false, error: detail, permanent: true };
    }
    const form = new FormData();
    form.append('file', photo, 'evidence.jpg');
    for (const [k, v] of Object.entries(item.body)) {
      if (v !== null && v !== undefined) form.append(k, String(v));
    }
    body = form;
    // No Content-Type: the browser has to set it, because only the browser
    // knows the multipart boundary it just generated.
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(item.body);
  }

  let response: Response;
  try {
    // apiUrl, never a relative path: relative would post to the SPA host,
    // which answers index.html with a 200 — and every replayed capture would
    // "succeed" and be dropped without reaching the API.
    response = await fetch(apiUrl(`/api${item.endpoint}`), {
      method: item.method,
      headers,
      body,
    });
  } catch (e) {
    const message = e instanceof Error ? e.message : 'network error';
    recordFailure(uid, item.id, message);
    return { sent: false, error: message };
  }

  if (response.ok) {
    removeConfirmed(uid, item.id);
    // Raw fetch, so the axios interceptor never sees this write: say it here,
    // or a checklist saved or an inspection completed through the queue would
    // leave the screen showing the old state (New Bug Auto Refresh).
    notifyWrite(item.endpoint);
    // The body, for the callers whose next screen depends on it — the kiosk
    // cannot print a badge number it was not given. Unwrapped from the API's
    // { success, data, message } envelope, so a caller reads the same shape it
    // would have got from apiPost. A body that will not parse is not a failed
    // send: the server confirmed it, which is the only thing the queue rule
    // turns on.
    let data: unknown;
    let message: string | undefined;
    try {
      const parsed = await response.json();
      data = parsed && typeof parsed === 'object' && 'data' in parsed
        ? (parsed as { data: unknown }).data
        : parsed;
      const m = parsed && typeof parsed === 'object' ? (parsed as { message?: unknown }).message : undefined;
      message = typeof m === 'string' ? m : undefined;
    } catch {
      data = undefined;
    }
    return { sent: true, data, message };
  }

  let detail = `HTTP ${response.status}`;
  try {
    const body = await response.json();
    // 🔴 A 422 detail is a LIST of {loc, msg}. String() of it read
    // "[object Object],[object Object]" in the Complete modal's toast
    // (Deep-test MNT-014); formatted the way the axios interceptor formats
    // every other call, so each sentence names its field.
    if (Array.isArray(body?.detail)) detail = formatDetailList(body.detail) || detail;
    else if (body?.detail) detail = String(body.detail);
  } catch {
    /* a body that will not parse leaves the status as the message */
  }

  // Retryable: no connection, an expired token (401 — the refresh fixes it),
  // a timeout, a rate limit, anything the server broke on — and 425, which is
  // the server saying an identical request of ours is still in flight and to
  // come back shortly. 409 is deliberately NOT here: sixty-six routes use it
  // for a real conflict that should stop the retry, so it cannot also mean
  // "again, shortly". A 4xx that is none of these is a body the server will
  // never accept, and retrying it for ever would leave a banner nobody can clear.
  //
  // 🔴 403 IS PERMANENT (FM Technician A16). It used to retry for ever: a
  // technician whose job was moved to someone else captured work offline, the
  // replay was refused every minute, and the work silently never arrived
  // (RESEARCH.md #1). Waiting does not grant a permission. It goes to the
  // blocked list with the server's reason, where the person can see it.
  const permanent =
    response.status >= 400 && response.status < 500 &&
    ![401, 408, 425, 429].includes(response.status);
  // Reception R4: a tablet revoked, or given a new code. It goes back to the code
  // screen rather than telling every visitor "saved" for ever.
  if (response.status === 401 && isKioskDevicePath(item.endpoint)) clearKioskDevice();

  if (permanent) {
    rejected.push({ ...item, uid, lastError: detail });
    block(uid, item, detail, response.status);
    if (replayRefused && (item.attempts > 0 || item.deferred) && (response.status === 409 || response.status === 422)) {
      try { replayRefused(item, detail, response.status); } catch { /* the blocked list still has it */ }
    }
  } else if (response.status >= 500) {
    // R2-3 #2: a server error in a row too many, or on day-old work, stops
    // holding the queue — moved to blocked with its last error, and told.
    const failures = (getPending(uid).find((i) => i.id === item.id)?.serverFailures ?? 0) + 1;
    const stale = Date.now() - item.capturedAt > SERVER_FAILURE_MAX_AGE_MS;
    if (failures >= SERVER_FAILURE_LIMIT || stale) {
      const why = `${SERVER_KEPT_FAILING} Last answer: ${detail}`;
      if (block(uid, item, why, response.status, { heldBack: true })) {
        rejected.push({ ...item, uid, lastError: why });
        return { sent: false, error: why, permanent: true };
      }
    }
    recordFailure(uid, item.id, detail, true);
  } else {
    recordFailure(uid, item.id, detail);
  }
  return { sent: false, error: detail, permanent };
}

/**
 * Capture something and try to send it immediately.
 *
 * THE ORDER IS THE POINT: it is written to the queue BEFORE the request, so a
 * tab that dies mid-flight still has the record, and it is removed only when
 * the server confirms. A caller that gets `sent: false` should tell the person
 * their work is saved but not sent — it is safe on the device and will go on
 * the next flush.
 *
 * Throws QueueFullError if the device has no room, in which case the capture
 * must be refused rather than attempted.
 */
export async function captureAndSend(
  item: Omit<PendingItem, 'id' | 'capturedAt' | 'attempts'>,
): Promise<SendResult> {
  const queued = enqueue(item);
  const uid = owner();
  if (!uid) return { sent: false, error: 'Signed out' };
  const r = await sendInOrder(uid, queued);
  // It went: anything queued behind it while it was out (a finish deferred
  // behind its photos) follows now, in order, rather than on the next minute.
  if (r.sent && getPending(uid).length > 0) void flush();
  return r;
}

/** Said of an item kept because older work of the same person is still waiting. */
export const BEHIND_OLDER = 'Waiting for earlier unsent work on this phone to go first.';

/**
 * 🔴 IN ORDER (FM Technician review R2-2). One person's captures replay in the
 * order they were made: a finish must not overtake the photos it needs, nor a
 * second checklist save the first. So an item is only sent from the HEAD of
 * the queue. Not at the head — older work is still waiting — it is not
 * attempted at all: it stays queued and an ordered flush is started, which
 * sends the head first and stops at the first item that neither went nor was
 * refused. The answer for the item is read off the lists afterwards.
 */
async function sendInOrder(uid: string, item: PendingItem): Promise<SendResult> {
  const head = getPending(uid)[0];
  if (head && head.id === item.id) {
    return attempt(uid, item);
  }
  awaited.set(item.id, null);
  try {
    // 🔴 A flush already running (the minute timer, a reconnect) is JOINED,
    // not skipped (review R2-3 #1): flush() hands back that flush's promise.
    // It may have read past this item's place before it was queued, or stopped
    // short of it — so if the item is still waiting and that flush never tried
    // it, flush again. A flush THIS call started is the ordered attempt; its
    // result stands (a head that failed is not retried at once).
    for (let round = 0; round < 5; round += 1) {
      const joined = flushActive;
      await flush();
      const untried = awaited.get(item.id) === null && getPending(uid).some((i) => i.id === item.id);
      if (!joined || !untried) break;
    }
    return resultFor(uid, item.id, awaited.get(item.id) ?? null);
  } finally {
    awaited.delete(item.id);
  }
}

/** What became of an item after an ordered flush: its own answer when this
 *  flush sent it, else read off the two lists. */
function resultFor(uid: string, id: string, answer: SendResult | null): SendResult {
  if (answer?.sent) return answer;
  const waiting = getPending(uid).find((i) => i.id === id);
  if (waiting) return { sent: false, error: waiting.lastError || BEHIND_OLDER };
  const refused = getBlocked(uid).find((b) => b.id === id);
  if (refused) return { sent: false, error: refused.lastError, permanent: true };
  return { sent: true };
}

/**
 * Send ONE waiting item now, because a person asked.
 *
 * The same `attempt` every other path uses, so the rule holds: it leaves only
 * on a 2xx, and a refusal moves it to blocked rather than deleting it. Not a
 * shortcut around flush — just a way to act on one row without waiting for the
 * minute timer or the next reconnect.
 */
export async function retryOne(id: string): Promise<SendResult> {
  const uid = owner();
  const item = uid ? getPending(uid).find((i) => i.id === id) : undefined;
  if (!uid || !item) return { sent: false, error: 'That item is no longer waiting.' };
  // In order (R2-2): pressing Send on a later row sends what is ahead of it first.
  return sendInOrder(uid, item);
}

/**
 * Where an item put back in the queue goes (review R2-4 #3): at its CAPTURE
 * position — before the first waiting item captured after it — not at the
 * tail, where it would go after work captured later and break the person's
 * order (a first checklist save landing after the second). Never ahead of an
 * item whose request is out: that one has already gone.
 */
function placeByCapture(queue: PendingItem[], item: PendingItem): PendingItem[] {
  let floor = 0;
  queue.forEach((q, i) => { if (inFlight.has(q.id)) floor = i + 1; });
  let at = queue.findIndex((q, i) => i >= floor && q.capturedAt > item.capturedAt);
  if (at < 0) at = queue.length;
  return [...queue.slice(0, at), item, ...queue.slice(at)];
}

/**
 * Put a refused capture back in the queue and try it again.
 *
 * For the case the blocked list exists to serve: the server said no, a person
 * fixed whatever it objected to — the duplicate, the permission, the building
 * that had been deleted — and wants the work to go now. It keeps its original
 * idempotency key, so if the earlier attempt DID land despite the error, the
 * server replays that answer instead of creating a second row.
 *
 * If the server refuses it again it goes straight back to blocked, with the
 * attempt count carried forward.
 */
export async function retryBlocked(id: string): Promise<SendResult> {
  const uid = owner();
  const entry = uid ? getBlocked(uid).find((b) => b.id === id) : undefined;
  if (!uid || !entry) return { sent: false, error: 'That item is no longer here.' };

  // Rebuilt field by field rather than spread-minus-two: a future field on
  // BlockedItem would otherwise ride back into the queue silently, and the
  // queue's shape is what every other path in here trusts.
  const item: PendingItem = {
    id: entry.id,
    label: entry.label,
    endpoint: entry.endpoint,
    method: entry.method,
    body: entry.body,
    capturedAt: entry.capturedAt,
    attempts: entry.attempts,
    ...(entry.blobRef ? { blobRef: entry.blobRef } : {}),
    ...(entry.idempotencyKey ? { idempotencyKey: entry.idempotencyKey } : {}),
    ...(entry.lastError ? { lastError: entry.lastError } : {}),
  };
  try {
    write(uid, placeByCapture(getPending(uid), item));
  } catch {
    return { sent: false, error: 'This device has no room to queue it again.' };
  }
  writeBlocked(uid, getBlocked(uid).filter((b) => b.id !== id));
  return sendInOrder(uid, item);
}

/**
 * Delete photos no queue item refers to any more.
 *
 * They only appear when something went wrong between storing a photo and
 * queueing it — a tab closed in the half-second between the two. Rare, and
 * permanent if nobody sweeps: nothing else in the app knows this store exists.
 * Run once on app open, where a few milliseconds of IndexedDB does not matter.
 */
export async function sweepOrphanPhotos(): Promise<number> {
  const { deletePhoto: drop, photoIds, photoBelongsTo } = await import('./pendingBlobs');
  const uid = owner();
  // Signed out: whose photos are orphans cannot be known, so none are.
  if (!uid) return 0;
  // BOTH lists. A blocked item still owns its photograph — it can be retried
  // once whatever the server objected to is fixed, and sweeping the image out
  // from under it would leave a retry that files evidence with no evidence in
  // it. Counting only the queue here would have deleted every blocked photo on
  // the next app open.
  const referenced = new Set(
    [...getPending(uid), ...getBlocked(uid)].map((i) => i.blobRef).filter(Boolean),
  );
  const stored = await photoIds();
  // Only this person's photos (and ownerless ones from before A16, whose items
  // were adopted above): another person's photos belong to THEIR queue, which
  // is not read here, so they would all look unreferenced.
  const orphans = stored.filter((id) => photoBelongsTo(id, uid) && !referenced.has(id));
  await Promise.all(orphans.map((id) => drop(id)));
  return orphans.length;
}

/** The flush running now, if any — returned to anyone who asks for a flush
 *  meanwhile (review R2-3 #1). */
let flushRunning: Promise<FlushResult> | null = null;
let flushActive = false;

/**
 * Try to send everything waiting — IN ORDER.
 *
 * An item is removed on a 2xx and moved to blocked ON A 4xx THAT IS NOT 401 —
 * a body the server will never accept cannot be retried into acceptance, and
 * keeping it forever would mean a banner nobody can ever clear. Its failure is
 * surfaced. Everything else — network error, 401, 5xx, 425 — is kept and tried
 * again. Only the signed-in person's own list is sent (A16).
 *
 * 🔴 STRICT FIFO PER PERSON (review R2-2). The flush works from the head and
 * STOPS at the first item that was neither sent nor refused — and at an item
 * whose request is already out (in flight), which it does not send twice. It
 * used to try every item regardless, so a finish queued behind a photo whose
 * upload had failed (or was still out) went first and was refused for the
 * photo it was about to have, and a second checklist save overtook the first.
 * A refused item is out of the way (in blocked) and the flush goes on to the
 * next. The queue is re-read at each step, so work queued while a flush runs
 * is reached by that same flush.
 */
export function flush(): Promise<FlushResult> {
  const uid = owner();
  if (!uid) return Promise.resolve({ sent: 0, kept: 0, refused: 0, uid: null });
  // One flush at a time; a second caller waits for the one running (R2-3 #1)
  // rather than being told at once — its own item may be in that flush.
  if (flushActive && flushRunning) return flushRunning;
  flushRunning = runFlush(uid);
  return flushRunning;
}

async function runFlush(uid: string): Promise<FlushResult> {
  // Set and cleared SYNCHRONOUSLY with the queue reads: a flush asked for
  // after this one's last read must start afresh, not join one that has
  // already decided there is nothing left.
  flushActive = true;
  let sent = 0;
  let refused = 0;
  const tried = new Set<string>();
  try {
    for (;;) {
      const item = getPending(uid)[0];
      // Nothing left; the head is already out (do not send it twice); or the
      // head came round again this pass (a refusal that could not be moved
      // out — no room — must not loop for ever).
      if (!item || inFlight.has(item.id) || tried.has(item.id)) break;
      tried.add(item.id);
      const r = await attempt(uid, item);
      if (r.sent) sent += 1;
      else if (r.permanent) refused += 1;
      else break;
    }
  } catch {
    /* a throw mid-flush keeps everything queued; the next flush tries again */
  } finally {
    flushActive = false;
  }
  return { sent, kept: getPending(uid).length, refused, uid };
}

/**
 * Flush on the two moments that matter, plus a slow heartbeat.
 *
 * "When the user happens to revisit the page that queued it" is not a trigger.
 * Reconnecting and opening the app are the moments someone's unsent work
 * should leave, and the interval is the backstop for a session left open on a
 * flaky connection.
 */
export function initPendingQueue(onFlush?: (r: FlushResult) => void): () => void {
  // 🔴 ONCE PER REAL FLUSH (review R2-4 #1). A run that lands while a flush is
  // out (the minute timer and a reconnect together) JOINS it and gets the same
  // promise back — and both used to report it: "2 saved items sent", twice.
  // The flush is reported by the first run that holds it, never again.
  const reported = new WeakSet<Promise<FlushResult>>();
  const run = () => {
    const running = flush();
    if (reported.has(running)) return;
    reported.add(running);
    void running.then((r) => {
      // `refused` is in this condition for a reason worth keeping. A flush
      // whose only item was REFUSED ends with sent = 0 and kept = 0, so the
      // old `sent > 0 || kept > 0` never fired — which meant takeRejected()
      // was never drained and the toast never appeared. The capture was
      // removed from the queue and the person was told nothing at all. That
      // was the one path in the whole mechanism that could still lose work in
      // silence, and it needed all three counts to close.
      if (onFlush && (r.sent > 0 || r.kept > 0 || r.refused > 0)) onFlush(r);
    });
  };

  window.addEventListener('online', run);
  const timer = window.setInterval(run, 60_000);
  // On app open, once, after the token is available.
  run();
  // And tidy any photo left behind by a tab that died between storing one and
  // queueing the item that owns it. Never on a timer: the only thing that can
  // create an orphan is a crash, and the only safe moment to decide a photo is
  // unreferenced is when no capture is half-finished.
  void sweepOrphanPhotos();

  return () => {
    window.removeEventListener('online', run);
    window.clearInterval(timer);
  };
}
